import {Component, Input} from "@angular/core";
import {RouteHelper, RouteMapping} from "../misc/route-helper";
import {ActivatedRoute, Params, Router} from "@angular/router";
import {ConfirmDialogService} from "../confirm-dialog/confirm-dialog-service";
import {contactTypes} from "../types/contact";
import {handleErrors} from "../misc/handle-errors.decorator";
import {SiteDetailModel, siteDetailModelFactory} from "../sites/site-detail.model";
import {SiteService} from "../sites/site.service";
import {ContactService} from "../contact/contact.service";
import {BuildingModel} from "./building.model";
import {FloorModel} from "./floor.model";
import {BuildingService} from "./building.service";
import {addressModelFactory} from "../address/address.model";
import {AddressService} from "../address/address.service";
import {contactModelFactory} from "../contact/contact.model";

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_LIMIT = 10;

@Component({
  selector: "project",
  templateUrl: "./building.template.pug",
  providers: [
    SiteService,
    BuildingService,
    ConfirmDialogService,
    ContactService,
    AddressService,
  ],
})

export class BuildingComponent extends RouteHelper {
  error: Error;
  id: string;
  site: SiteDetailModel = siteDetailModelFactory();
  buildings: any = [];
  building: BuildingModel;
  contactTypes: any[] = contactTypes;
  hasContacts: boolean = false;
  availableContacts: any = [];
  currentPage: number = DEFAULT_PAGE;
  pageSize: number = DEFAULT_PAGE_LIMIT;
  maxPages: number;
  totalCount: number = 0;
  searchText: string = "";
  siteId: string;
  projectId: any;
  loading: boolean = true;
  createMode: boolean = false;
  deleteMode: boolean = false;
  deleteBuildings = [];
  addresses: any = [];

  defaultBuilding = new BuildingModel();
  @Input() newBuilding = new BuildingModel();

  paramsMap: RouteMapping[] = [
    {fieldName: "currentPage", paramName: "page", defaultValue: DEFAULT_PAGE, type: "number"} as RouteMapping,
    {fieldName: "pageSize", paramName: "limit", defaultValue: DEFAULT_PAGE_LIMIT, type: "number"} as RouteMapping,
    {fieldName: "searchText", paramName: "search"} as RouteMapping,
    {fieldName: "siteId", paramName: "siteId"} as RouteMapping,
  ];

  constructor(
    public route: ActivatedRoute,
    public router: Router,
    private buildingService: BuildingService,
    private siteService: SiteService,
    public confirmDialogService: ConfirmDialogService,
  ) {
    super();
  }

  @handleErrors()
  async loadData() {
    this.loading = true;

    if (!this.siteId) {
      throw new Error("Please go back to the main page and try again.");
    }

    this.site = await this.siteService.getSiteById(this.siteId);
    this.hasContacts = this.contactTypes.some(type => this.site[type.field]);

    this.projectId = this.site.projectId;

    const {rows, count, floors} = await this.buildingService.getBuildings(
      +(this.currentPage) - 1,
      +(this.pageSize),
      this.searchText,
      this.siteId,
      this.projectId,
    );

    this.buildings = this.matchFloors(rows, floors);
    this.maxPages = Math.ceil(count / (+this.pageSize));
    this.totalCount = count;

    this.availableContacts = await this.populateContacts();

    this.loading = false;
  }

  async populateContacts() {
    const populatedContacts = [];

    const siteContacts = [
      this.site.alternateContact,
      this.site.breakfixContact,
      this.site.deliveryContact,
      this.site.installationContact,
      this.site.itContact,
      this.site.managerContact,
      this.site.siteContact,
      this.site.tonerContact,
    ];

    let duplicateContact = false;

    for (const contact of siteContacts) {
      if (contact.firstName) {
        duplicateContact = false;
        for (const compareContact of populatedContacts) {
          if (contact.firstName === compareContact.firstName && contact.emailAddress === compareContact.emailAddress) {
            duplicateContact = true;
          }
        }
        if (!duplicateContact) {
          populatedContacts.push(contact);
        }
      }
    }

    for (const building of this.buildings) {
      const buildingContacts = [];
      if (building.tonerContact) buildingContacts.push(building.tonerContact);
      if (building.breakfixContact) buildingContacts.push(building.breakfixContact);
      for (const contact of buildingContacts) {
        if (contact.firstName) {
          duplicateContact = false;
          for (const compareContact of populatedContacts) {
            if (
              contact.firstName === compareContact.firstName &&
              contact.emailAddress === compareContact.emailAddress
            ) {
              duplicateContact = true;
            }
          }
          if (!duplicateContact) {
            populatedContacts.push(contact);
          }
        }
      }
    }
    return populatedContacts;
  }

  matchFloors(buildings, floors) {
    for (const floor of floors) {
      for (const building of buildings) {
        if (building.id === floor.buildingId) {
          building.floors.push(floor);
          building.tempFloors.push(floor);
          break;
        }
      }
    }
    return buildings;
  }

  navigate(queryParams: Params, skipLocationChange: boolean = false): Promise<boolean> {
    return this.router.navigate(["building"], {queryParams, skipLocationChange});
  }

  create() {
    this.createMode = true;
  }

  cancel() {
    this.createMode = false;
    this.newBuilding = this.defaultBuilding;
  }

  save(building) {
    this.processCreateData(building);
    this.createMode = false;
    this.saveBuilding(building);
    this.loadData();
  }

  editBuilding(building, field) {
    switch (field) {
      case "physicalAddress":
        building.physicalAddressForm.controls["name1"].setValue(building.name);
        break;
      case "tonerAddress":
        building.tonerAddressForm.controls["name1"].setValue(building.name);
        break;
      case "serviceAddress":
        building.serviceAddressForm.controls["name1"].setValue(building.name);
        break;
      default:
        break;
    }
    building.enableEdit = true;
    building.currentIndex = field;
  }

  cancelEdit(building) {
    building.enableEdit = false;
  }

  saveEdit(building, field) {
    const cleanBuilding = new BuildingModel();
    cleanBuilding.id = building.id;
    cleanBuilding.name = building.name;
    switch (field) {
      case "name":
        cleanBuilding.name = building.tempName;
        break;
      case "physicalAddress":
        cleanBuilding.siteAddress = addressModelFactory(building.physicalAddressForm.value);
        break;
      case "tonerAddress":
        cleanBuilding.tonerAddress = addressModelFactory(building.tonerAddressForm.value);
        break;
      case "serviceAddress":
        cleanBuilding.serviceAddress = addressModelFactory(building.serviceAddressForm.value);
        break;
      case "tonerContact":
        if (building.tempTonerContact === "newContact") {
          cleanBuilding.tonerContact = contactModelFactory(building.tonerContactForm.value);
        } else if (building.tempTonerContact) {
          cleanBuilding.tonerContact = building.tempTonerContact;
        }
        break;
      case "serviceContact":
        if (building.tempBreakfixContact === "newContact") {
          cleanBuilding.breakfixContact = contactModelFactory(building.serviceContactForm.value);
        } else if (building.tempBreakfixContact) {
          cleanBuilding.breakfixContact = building.tempBreakfixContact;
        }
        break;
      case "floors":
        cleanBuilding.floors = building.floors;
        cleanBuilding.tempFloors = building.tempFloors;
        break;
      default: break;
    }
    this.save(cleanBuilding);
  }

  addFloors(source) {
    const additionalFloor = new FloorModel();
    source.push(additionalFloor);
  }

  removeFloor(source, floor) {
    const index = source.indexOf(floor);
    source.splice(index, 1);
  }

  processCreateData(building) {
    if (!building.siteId) building.siteId = this.siteId;
    if (!building.projectId) building.projectId = this.projectId;
    if (building.physicalAddressForm) delete building.physicalAddressForm;
    if (building.tonerAddressForm) delete building.tonerAddressForm;
    if (building.serviceAddressForm) delete building.serviceAddressForm;
    if (building.tonerContactForm) delete building.tonerContactForm;
    if (building.serviceContactForm) delete building.serviceContactForm;
    if (building.tempBreakfixContact) delete building.tempBreakfixContact;
    if (building.tempTonerContact) delete building.tempTonerContact;
  }

  deleteEnable() {
    this.deleteMode = true;
  }

  deleteAdd(building) {
    this.deleteBuildings.push(building.id);
    building.deleteFlag = true;
  }

  deleteRemove(building) {
    const index = this.deleteBuildings.indexOf(building.id);
    this.deleteBuildings.splice(index, 1);
    building.deleteFlag = false;
  }

  deleteCancel() {
    this.deleteMode = false;
  }

  deleteConfirm() {
    this.buildingService.remove(this.siteId, this.deleteBuildings);
    this.deleteMode = false;
    this.loadData();
  }

  onListControlsChange(event) {
    switch (event["key"]) {
      case "page":
        this.currentPage = event["value"];
        break;
      case "search":
        this.currentPage = DEFAULT_PAGE;
        this.searchText = event["value"];
        break;
      case "limit":
        this.currentPage = DEFAULT_PAGE;
        this.pageSize = event["value"];
        break;
      default:
        console.info(`Unknown ${event["key"]}`);
        break;
    }

    this.syncRoute();
  }

  @handleErrors()
  async saveBuilding(building): Promise<boolean> {
    await this.buildingService.saveBuilding(building);
    return true;
  }
}